import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { api, type StorageWarning } from "../api";
import { useRole } from "../hooks/useRole";
import { Modal } from "./Modal";

const STORAGE_KEY = "bf-storage-retention-warn";

function limitPhrase(limit: StorageWarning["limit"]): string {
  if (limit === "project") return "the project limit";
  if (limit === "both") return "the detection and project limits";
  return "the detection limit";
}

function warningIdentity(w: StorageWarning, asOfMs: number | undefined): string {
  return [
    asOfMs ?? 0,
    w.used_bytes,
    w.max_bytes,
    w.retention_days,
    w.limit ?? "detection",
  ].join(":");
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private browsing / storage disabled */
  }
}

export function StorageRetentionModal() {
  const { isAdmin, isLoading } = useRole();
  const nav = useNavigate();
  const [dismissedId, setDismissedId] = useState<string | null>(readDismissed);

  const usage = useQuery({
    queryKey: ["storage-usage"],
    queryFn: api.admin.storage,
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const warning = usage.data?.warning ?? null;
  const identity = useMemo(
    () => (warning ? warningIdentity(warning, usage.data?.as_of_ms) : null),
    [warning, usage.data?.as_of_ms],
  );
  const open = Boolean(isAdmin && warning && identity && identity !== dismissedId);

  useEffect(() => {
    setDismissedId(readDismissed());
  }, [identity]);

  const dismiss = useCallback(() => {
    if (!identity) return;
    writeDismissed(identity);
    setDismissedId(identity);
  }, [identity]);

  if (isLoading || !open || !warning) return null;

  return (
    <Modal onClose={dismiss} labelledBy="storage-retention-title" panelClassName="w-full max-w-lg">
      <div className="space-y-4">
        <h2 id="storage-retention-title" className="text-lg font-medium text-white">
          Detections will be removed early
        </h2>
        <p className="text-sm text-bf-text">
          The size limit is about to remove detections before they reach the
          keep period. About {warning.history_days_kept} days are stored now;
          the goal is {warning.retention_days} days.{" "}
          {warning.limit === "both"
            ? "The detection and project limits are forcing that early removal."
            : `The ${limitPhrase(warning.limit).replace(/^the /, "")} is forcing that early removal.`}
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" className="bf-button bg-white/10 bg-none" onClick={dismiss}>
            Dismiss
          </button>
          <button
            type="button"
            className="bf-button"
            onClick={() => {
              dismiss();
              nav({ to: "/settings", search: { section: "storage" } });
            }}
          >
            Storage settings
          </button>
        </div>
      </div>
    </Modal>
  );
}
